"use client";

import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import {
  PeeqdReportTabId,
  PeeqdContentTabId,
  PEEQD_REPORT_TABS,
  PEEQD_CONTENT_TABS,
  PEEQD_SECTION_DEFAULT_CONTENT_TAB,
  getDefaultReportTab,
  PeeqdToolId,
  PEEQD_COMING_SOON_SECTIONS,
  isComingSoonSection,
} from "@/config/peeqdContent";
import { ReportTabIcon, ContentTabIcon } from "./icons";
import ListAvatar from "@/components/peeqd/tracking/ListAvatar";
import ComingSoonCard from "@/components/peeqd/ComingSoonCard";
import { peeqdAvatarUrl } from "@/libs/peeqdAvatars";
import PeeqdProfileHeader from "./PeeqdProfileHeader";
import { buildProfilePreview, type PeeqdProfileData } from "@/libs/peeqdProfilePreview";
import { FOLLOW_LIST_DISPLAY_LIMIT } from "@/libs/tracking/constants";
import { INSTAGRAM_LINK_PROPS, instagramProfileUrl } from "@/libs/instagramUrl";
import ChangesSummaryCard from "@/components/peeqd/tracking/ChangesSummaryCard";
import type { WeekProfileSummaryData } from "@/libs/tracking/weekProfileSummary";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { redirectTo, REDIRECT_MESSAGES } from "@/libs/redirectLoading";
import { trackFunnelEvent } from "@/libs/ga";

export { FOLLOW_LIST_DISPLAY_LIMIT };

type Side = "followers" | "following";
export type FollowFilter = "new" | "lost" | "all";

interface ListItem {
  u: string;
  n: string;
  img: number;
  status?: "new" | "lost";
}

interface SideStats {
  total: number;
  gained: number;
  lost: number;
}

const DEFAULT_USERNAME = "otherside.studio";

const STORY_GRADIENTS = [
  "from-rose/80 via-violet/70 to-rose-deep/80",
  "from-violet/70 via-rose/60 to-ink/70",
  "from-mint/60 via-rose/50 to-violet/70",
  "from-rose-deep/70 via-rose/60 to-violet/80",
  "from-ink/60 via-violet/50 to-rose/70",
  "from-rose/70 via-mint/40 to-violet/60",
];

const PREVIEW_SIDE_STATS: Record<Side, SideStats> = {
  followers: { total: 1247, gained: 12, lost: 2 },
  following: { total: 843, gained: 5, lost: 1 },
};

const followersList: ListItem[] = [
  { u: "v3li3e", n: "nika lischenko", img: 28, status: "new" },
  { u: "olesya.ww", n: "Olesya Vladimirovna", img: 45, status: "new" },
  { u: "1a.lola", n: "lola", img: 32, status: "new" },
  { u: "_kh_andy_", n: "Khamitov", img: 12, status: "lost" },
  { u: "ksenia_med", n: "Ksenia Medushevskaya", img: 20, status: "lost" },
];

const followingList: ListItem[] = [
  { u: "travelwithnora", n: "Nora Lindqvist", img: 47, status: "new" },
  { u: "mattjdesign", n: "Matt J", img: 33, status: "lost" },
  { u: "localfoods.co", n: "Local Foods Co", img: 14, status: "new" },
  { u: "type.daily", n: "Type Daily", img: 52, status: "new" },
  { u: "grid.press", n: "Grid Press", img: 52, status: "lost" },
];

/** Homepage showcase only — matches production scroll UX without touching paid reports. */
const SHOWCASE_FOLLOW_LIST_LIMIT = 50;

function buildShowcaseFollowList(side: Side): ListItem[] {
  const seed = side === "following" ? followingList : followersList;
  const { gained, lost } = PREVIEW_SIDE_STATS[side];
  const neutralCount = Math.max(0, SHOWCASE_FOLLOW_LIST_LIMIT - gained - lost);
  const items: ListItem[] = [];

  for (let i = 0; i < gained; i++) {
    const base = seed[i % seed.length];
    items.push({
      u: i < seed.length ? base.u : `${base.u}_n${i + 1}`,
      n: base.n,
      img: (base.img + i) % 60,
      status: "new",
    });
  }
  for (let i = 0; i < lost; i++) {
    const base = seed[(i + gained) % seed.length];
    items.push({
      u: `${base.u}_l${i + 1}`,
      n: base.n,
      img: (base.img + gained + i) % 60,
      status: "lost",
    });
  }
  for (let i = 0; i < neutralCount; i++) {
    const base = seed[i % seed.length];
    items.push({
      u: `${base.u}_a${i + 1}`,
      n: base.n,
      img: (base.img + gained + lost + i) % 60,
    });
  }
  return items;
}

const storyItems = [
  { mentions: 3, links: 0, time: "Jul 7, 2026 14:39:29", gradient: 0 },
  { mentions: 1, links: 2, time: "Jul 6, 2026 09:12:04", gradient: 1 },
  { mentions: 0, links: 1, time: "Jul 5, 2026 18:44:11", gradient: 2 },
];

const postItems = [
  { likes: "1.2k", comments: 48, caption: "New brand drop", gradient: 0 },
  { likes: "892", comments: 31, caption: "Summer collection", gradient: 1 },
  { likes: "2.4k", comments: 112, caption: "Studio tour", gradient: 2 },
  { likes: "634", comments: 19, caption: "Behind the scenes", gradient: 3 },
  { likes: "3.1k", comments: 87, caption: "Client spotlight", gradient: 4 },
  { likes: "1.8k", comments: 54, caption: "Process reel stills", gradient: 5 },
];

const reelItems = [
  { views: "12.4k", duration: "0:32", gradient: 0 },
  { views: "8.7k", duration: "0:18", gradient: 1 },
  { views: "21.1k", duration: "0:45", gradient: 2 },
  { views: "5.2k", duration: "0:24", gradient: 3 },
];

const highlightItems = [
  { label: "Rare Beauty", gradient: 0, img: 20 },
  { label: "S+C", gradient: 1, img: 21 },
  { label: "Revelación", gradient: 2, img: 22 },
  { label: "#BLM 2", gradient: 3, img: 23 },
  { label: "#BLM", gradient: 4, img: 24 },
  { label: "Rare", gradient: 5, img: 25 },
  { label: "2019", gradient: 0, img: 26 },
  { label: "ISILYF", gradient: 1, img: 27 },
];

const repostItems = [
  { original: "type.foundry", caption: "New specimen drop", time: "2d ago", img: 22 },
  { original: "design.daily", caption: "Grid systems in practice", time: "4d ago", img: 38 },
  { original: "motion.lab", caption: "30s motion loop", time: "1w ago", img: 11 },
];

const likesLeaderboard = [
  { place: 1, user: "carolebamford", name: "Carole Bamford", img: 15, likes: 11, trend: "same" as const, thumbs: [12, 23, 34, 45, 56, 67, 19, 20, 31, 42, 53] },
  { place: 2, user: "iandore", name: "Ian Dore", img: 12, likes: 6, trend: "up" as const, trendBy: 5, thumbs: [15, 26, 37, 48, 59, 60] },
  { place: 3, user: "coco.suze", name: "Coco Suze", img: 32, likes: 5, trend: "up" as const, trendBy: 2, thumbs: [11, 22, 33, 44, 55] },
  { place: 4, user: "portiaportia", name: "Portia Portia", img: 28, likes: 4, trend: "up" as const, trendBy: 4, thumbs: [14, 25, 36, 47] },
  { place: 5, user: "sirsac", name: "Sir Sac", img: 41, likes: 4, trend: "same" as const, thumbs: [17, 28, 39, 50] },
  { place: 6, user: "design.daily", name: "Design Daily", img: 22, likes: 3, trend: "up" as const, trendBy: 1, thumbs: [18, 29, 40] },
  { place: 7, user: "motion.lab", name: "Motion Lab", img: 38, likes: 3, trend: "same" as const, thumbs: [21, 32, 43] },
];

const LIKES_TOTAL = 109;

const SHOWCASE_LOOKUP_ERROR =
  "We couldn't load this profile right now. Please try again in a few minutes.";

function friendlyLookupError(raw: string): string {
  if (/hikerapi|profile lookup failed/i.test(raw)) {
    return SHOWCASE_LOOKUP_ERROR;
  }
  return raw;
}

const SHOWCASE_SUMMARY: WeekProfileSummaryData = {
  weekLabel: "Jun 30 – Jul 6, 2026",
  username: DEFAULT_USERNAME,
  handle: `@${DEFAULT_USERNAME}`,
  activityLevel: "moderately active",
  mediaLikes: LIKES_TOTAL,
  followingsGiven: PREVIEW_SIDE_STATS.following.gained,
  likedUsersCount: 28,
  avgLikesPerUser: 3.89,
  verifiedLikedCount: 26,
  favoriteUser: { username: "carolebamford", likes: 11 },
  hashtags: ["#design", "#branding", "#studio"],
  interestCategories: ["Art & Culture", "Fashion"],
  gainedFollowers: PREVIEW_SIDE_STATS.followers.gained,
  lostFollowers: PREVIEW_SIDE_STATS.followers.lost,
  gainedFollowing: PREVIEW_SIDE_STATS.following.gained,
  lostFollowing: PREVIEW_SIDE_STATS.following.lost,
  hasEngagementData: true,
  postsInPeriod: 3,
  postsAnalyzed: 3,
  usedPostFallback: false,
  noPostsInLast7Days: false,
  daysSinceLastPost: 2,
  avgLikesPerPost: 36.3,
};

function PostThumb({ img, gradient, compact = false }: { img?: number; gradient?: number; compact?: boolean }) {
  const sizeClass = compact ? "h-7 w-7" : "h-9 w-9";
  if (img != null) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={peeqdAvatarUrl(80, img)} alt="" className={`${sizeClass} shrink-0 rounded object-cover`} />
    );
  }
  const iconSize = compact ? "h-3 w-3" : "h-4 w-4";
  const boxSize = compact ? "h-7 w-7" : "h-6 w-6";
  return (
    <div className={`grid ${boxSize} shrink-0 place-items-center rounded bg-gradient-to-br ${STORY_GRADIENTS[gradient ?? 0]}`}>
      <svg className={`${iconSize} text-white/90`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /></svg>
    </div>
  );
}

function LikesPlaceBadge({ row }: { row: typeof likesLeaderboard[0] }) {
  return (
    <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
      <span className="w-4 text-center font-mono text-[12px] font-semibold text-ink sm:text-[13px]">{row.place}</span>
      {row.trend === "up" ? (
        <span className="flex items-center gap-0.5 text-[10px] font-semibold text-mint sm:text-[11px]">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m18 15-6-6-6 6" /></svg>
          {row.trendBy}
        </span>
      ) : (
        <span className="h-1.5 w-1.5 rounded-full bg-ink" />
      )}
    </div>
  );
}

function LikesCountBadge({ likes }: { likes: number }) {
  return (
    <span className="flex shrink-0 items-center gap-1 text-[12px] font-semibold text-ink sm:text-[13px]">
      <svg className="h-3.5 w-3.5 text-amber-400" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
      {likes}
    </span>
  );
}

function LikesLeaderboardThumbs({ row, compact = false }: { row: typeof likesLeaderboard[0]; compact?: boolean }) {
  return (
    <div className="no-bar flex gap-1 overflow-x-auto sm:max-w-[14rem]">
      {row.thumbs.map((t, i) => (
        <PostThumb key={i} img={i % 3 === 0 ? t : undefined} gradient={t % STORY_GRADIENTS.length} compact={compact} />
      ))}
    </div>
  );
}

function LikesLeaderboardRow({ row }: { row: typeof likesLeaderboard[0] }) {
  return (
    <div className="border-b border-line px-4 py-3 sm:px-5 sm:py-3.5">
      <div className="flex items-center gap-2 sm:hidden">
        <LikesPlaceBadge row={row} />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={peeqdAvatarUrl(64, row.img)} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-[13px] font-semibold text-ink">{row.user}</p>
          <p className="truncate text-[11px] text-muted">{row.name}</p>
        </div>
        <LikesCountBadge likes={row.likes} />
      </div>
      <div className="mt-2 pl-10 sm:hidden">
        <LikesLeaderboardThumbs row={row} compact />
      </div>

      <div className="hidden grid-cols-[5rem_1fr_auto] items-center gap-3 sm:grid">
        <LikesPlaceBadge row={row} />
        <div className="flex min-w-0 items-center gap-2.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={peeqdAvatarUrl(64, row.img)} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
          <div className="min-w-0 leading-tight">
            <p className="truncate text-[13.5px] font-semibold text-ink">{row.user}</p>
            <p className="truncate text-[12px] text-muted">{row.name}</p>
          </div>
        </div>
        <div className="flex min-w-0 items-center justify-end gap-2">
          <LikesCountBadge likes={row.likes} />
          <LikesLeaderboardThumbs row={row} />
        </div>
      </div>
    </div>
  );
}

const LOADING_LABELS: Record<PeeqdReportTabId, string> = {
  unfollows: "Fetching followers & following…",
  stories: "Loading stories…",
  comments: "Loading comments…",
  content: "Loading content…",
  likes: "Scanning like activity…",
};

const REVEAL_COPY: Record<PeeqdReportTabId, { title: string; body: string }> = {
  unfollows: { title: "Reveal all followers", body: "See every recent follower and unfollower, dated." },
  stories: {
    title: "Reveal & Track this Profile",
    body: "Get alerts on every new story - mentions, links and timestamps. Paid plans unlock the full archive.",
  },
  comments: {
    title: "Reveal & Track this Profile",
    body: "See every comment on their posts - who said what and when. Coming soon on Peeqd.",
  },
  content: {
    title: "Reveal & Track this Profile",
    body: "Monitor posts, reels, highlights and reposts as they go live. Paid plans unlock everything.",
  },
  likes: { title: "Reveal all 109 likes", body: "See every account that liked their posts - with post thumbnails." },
};

const REVEAL_CTA: Record<PeeqdReportTabId, string> = {
  unfollows: "Reveal All",
  stories: "Never Miss an Update",
  comments: "Never Miss an Update",
  content: "Never Miss an Update",
  likes: "Reveal All",
};

export function SectionPillTabs({
  active,
  onChange,
  align = "center",
  excludeTabs = [],
}: {
  active: PeeqdReportTabId;
  onChange: (tab: PeeqdReportTabId) => void;
  align?: "center" | "start";
  excludeTabs?: PeeqdReportTabId[];
}) {
  const tabs = PEEQD_REPORT_TABS.filter((tab) => !excludeTabs.includes(tab.id));

  return (
    <div className={`flex w-full ${align === "start" ? "justify-start" : "justify-center"}`}>
      <div className="w-fit max-w-full rounded-2xl border border-line bg-surface p-1.5 shadow-soft">
        <div className="no-bar flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-active={active === tab.id ? "true" : "false"}
              onClick={() => onChange(tab.id)}
              className="section-pill flex shrink-0 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-semibold text-muted transition sm:px-4 sm:text-[13px]"
            >
              <ReportTabIcon tab={tab.id} className="section-tab-icon h-[15px] w-[15px] shrink-0 sm:h-4 sm:w-4" />
              <span className="hidden min-[400px]:inline">{tab.label}</span>
              <span className="min-[400px]:hidden">{tab.shortLabel}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function UnderlineTabs<T extends string>({
  tabs,
  active,
  onChange,
  renderIcon,
  renderLabel,
}: {
  tabs: { id: T; label: string }[];
  active: T;
  onChange: (id: T) => void;
  renderIcon?: (id: T) => ReactNode;
  renderLabel: (tab: { id: T; label: string }) => ReactNode;
}) {
  const cls = "content-tab";

  return (
    <div className="border-b border-line">
      <div className="no-bar flex overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-active={active === tab.id ? "true" : "false"}
            onClick={() => onChange(tab.id)}
            className={`${cls} flex shrink-0 items-center gap-2 border-b-2 border-transparent px-4 py-3 text-[13px] font-semibold text-muted transition sm:px-5 sm:text-[13.5px]`}
          >
            {renderIcon?.(tab.id)}
            {renderLabel(tab)}
          </button>
        ))}
      </div>
    </div>
  );
}

function ProcessingBar({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-line bg-rose-softer px-4 py-3">
      <div className="relative h-1.5 overflow-hidden rounded-full bg-rose-soft">
        <div className="absolute inset-y-0 left-0 w-1/3 rounded-full grad-bg animate-[pv-progress_1.4s_ease-in-out_infinite]" />
      </div>
      <p className="mt-2 text-center text-[12px] font-medium text-rose-deep">{label}</p>
    </div>
  );
}

function RevealOverlay({
  tab,
  onUnlock,
}: {
  tab: PeeqdReportTabId;
  onUnlock?: () => void;
}) {
  const router = useRouter();
  const copy = REVEAL_COPY[tab];

  const handleUnlock = () => {
    if (onUnlock) {
      onUnlock();
      return;
    }
    router.push("/pricing");
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-surface/10 via-surface/75 to-surface px-4 py-8 sm:py-10">
      <div className="w-full max-w-sm rounded-3xl border border-line bg-surface/95 p-6 text-center shadow-card backdrop-blur">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full grad-bg text-white shadow-soft">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
        </div>
        <p className="mt-3 font-display text-[19px] font-extrabold leading-tight">{copy.title}</p>
        <p className="mt-1.5 text-[13px] text-muted">{copy.body}</p>
        <button
          type="button"
          onClick={handleUnlock}
          className="mt-4 inline-flex items-center gap-2 rounded-xl grad-bg px-5 py-2.5 text-[14px] font-semibold text-white shadow-card transition hover:opacity-95 active:scale-[.99]"
        >
          {REVEAL_CTA[tab]}
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
        <p className="mt-3 text-[11.5px] text-muted">Join 12,000+ marketers &amp; creators on Peeqd</p>
      </div>
    </div>
  );
}

function LockedBlock({ tab, children }: { tab: PeeqdReportTabId; children: ReactNode }) {
  return (
    <div className="relative min-h-[21rem]">
      <div className="blur-rows">{children}</div>
      <RevealOverlay tab={tab} />
    </div>
  );
}

/** Homepage showcase — single gatekeeper over all mock report content after a real profile peek. */
function ShowcaseGatekeeper({
  active,
  tab,
  onUnlock,
  children,
}: {
  active: boolean;
  tab: PeeqdReportTabId;
  onUnlock: () => void;
  children: ReactNode;
}) {
  if (!active) return <>{children}</>;

  return (
    <div className="relative min-h-[24rem]">
      <div className="blur-rows">{children}</div>
      <RevealOverlay tab={tab} onUnlock={onUnlock} />
    </div>
  );
}

function ShowcaseLookupError({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="relative z-10 overflow-hidden rounded-3xl border border-line/80 bg-surface/95 p-8 text-center shadow-card ring-1 ring-black/[0.03] sm:p-10">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-rose-softer text-rose-deep">
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
      <p className="mt-4 font-display text-[20px] font-extrabold text-ink">
        Couldn&apos;t load this profile
      </p>
      <p className="mx-auto mt-2 max-w-md text-[14px] leading-relaxed text-muted">{message}</p>
    </div>
  );
}
function ExportBanner() {
  return (
    <div className="flex flex-col items-start justify-between gap-3 border-b border-line bg-sky-50/80 px-4 py-3 sm:flex-row sm:items-center sm:px-5">
      <div>
        <p className="text-[13px] font-semibold text-ink">🔥 In one click</p>
        <p className="text-[11.5px] text-muted">Stories, Highlights, Mentions &amp; Links. Export to Excel.</p>
      </div>
      <button type="button" className="inline-flex shrink-0 items-center gap-2 self-end rounded-lg grad-bg px-4 py-2 text-[12.5px] font-semibold text-white shadow-soft sm:self-center">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
        Download all
      </button>
    </div>
  );
}

function TopMentions() {
  return (
    <div className="px-4 py-3 sm:px-5">
      <p className="mb-2.5 text-[12px] font-bold text-ink">
        Top Mentions <span className="font-normal text-muted">(@)</span>
      </p>
      <div className="no-bar flex gap-3 overflow-x-auto pb-1">
        <div className="flex shrink-0 flex-col items-center gap-1">
          <div className="grid h-11 w-11 place-items-center rounded-full border-2 border-rose bg-rose-softer text-[11px] font-bold text-rose-deep">All @</div>
        </div>
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="flex shrink-0 flex-col items-center gap-1">
            <div className="h-11 w-11 rounded-full bg-line" />
            <span className="font-mono text-[10px] text-muted">{n} @••••••</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StoryCard({ item, locked }: { item: typeof storyItems[0]; locked?: boolean }) {
  return (
    <div className="flex flex-col">
      <div className={`relative aspect-[9/16] overflow-hidden rounded-lg bg-gradient-to-br ${STORY_GRADIENTS[item.gradient]}`}>
        <div className="absolute bottom-2 left-2 flex gap-1">
          <span className="rounded bg-rose/90 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white">@ {item.mentions}</span>
          <span className="rounded bg-ink/70 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white">🔗 {item.links}</span>
        </div>
        {locked && (
          <div className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded bg-white/90">
            <svg className="h-3 w-3 text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          </div>
        )}
      </div>
      <p className="mt-1.5 text-center font-mono text-[10px] text-muted">{item.time}</p>
    </div>
  );
}

function StoriesGrid({ locked = false }: { locked?: boolean }) {
  const visible = locked ? storyItems.slice(0, 3) : storyItems;
  return (
    <div className="px-4 py-4 sm:px-5">
      <p className="mb-3 text-[12px] font-bold text-ink">
        Top Link Types <span className="font-normal text-muted">(sample)</span>
      </p>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-3">
        {visible.map((item, i) => (
          <StoryCard key={i} item={item} locked={locked && i >= 1} />
        ))}
      </div>
    </div>
  );
}

function PostsGrid() {
  return (
    <div className="grid grid-cols-3 gap-0.5 p-0.5">
      {postItems.map((item, i) => (
        <div key={i} className={`relative aspect-square bg-gradient-to-br ${STORY_GRADIENTS[item.gradient]}`}>
          <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-ink/70 to-transparent p-2">
            <p className="line-clamp-1 text-[9px] font-medium text-white">{item.caption}</p>
            <p className="font-mono text-[9px] text-white/70">♥ {item.likes}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReelsGrid() {
  return (
    <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 sm:px-5">
      {reelItems.map((item, i) => (
        <div key={i} className="flex flex-col">
          <div className={`relative aspect-[9/16] overflow-hidden rounded-lg bg-gradient-to-br ${STORY_GRADIENTS[item.gradient]}`}>
            <div className="absolute inset-0 grid place-items-center">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-white/25 backdrop-blur">
                <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              </div>
            </div>
            <span className="absolute bottom-2 right-2 rounded bg-ink/70 px-1.5 py-0.5 font-mono text-[10px] text-white">{item.duration}</span>
          </div>
          <p className="mt-1.5 text-center font-mono text-[10px] text-muted">{item.views} views</p>
        </div>
      ))}
    </div>
  );
}

function HighlightCircle({ label, gradient, img }: { label: string; gradient: number; img: number }) {
  const ring = STORY_GRADIENTS[gradient % STORY_GRADIENTS.length];

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`h-[70px] w-[70px] shrink-0 rounded-full bg-gradient-to-br p-[2.5px] ${ring}`}>
        <div className="h-full w-full overflow-hidden rounded-full">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={peeqdAvatarUrl(96, img)} alt="" className="h-full w-full object-cover" />
        </div>
      </div>
      <span className="max-w-[76px] truncate text-center text-[11px] leading-tight text-ink">{label}</span>
    </div>
  );
}

function HighlightsGrid() {
  return (
    <div className="p-4 sm:px-5">
      <div className="grid grid-cols-4 gap-x-4 gap-y-5 min-[480px]:grid-cols-6 sm:grid-cols-8">
        {highlightItems.map((h, i) => (
          <HighlightCircle key={i} label={h.label} gradient={h.gradient} img={h.img} />
        ))}
      </div>
    </div>
  );
}

function RepostsGrid() {
  return (
    <div className="divide-y divide-line">
      {repostItems.map((item) => (
        <div key={item.original} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
          <div className="relative shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={peeqdAvatarUrl(64, item.img)} alt="" className="h-10 w-10 rounded-lg object-cover" />
            <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-violet text-white">
              <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14" /></svg>
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold">Reposted from @{item.original}</p>
            <p className="truncate text-[12px] text-muted">{item.caption}</p>
          </div>
          <span className="shrink-0 font-mono text-[11px] text-muted">{item.time}</span>
        </div>
      ))}
    </div>
  );
}

function ContentBody({ tab, unlocked = false }: { tab: PeeqdContentTabId; unlocked?: boolean }) {
  switch (tab) {
    case "stories":
      return (
        <>
          <TopMentions />
          <StoriesGrid />
          {!unlocked && (
            <LockedBlock tab="stories">
              <StoriesGrid locked />
            </LockedBlock>
          )}
        </>
      );
    case "posts":
      return (
        <>
          <PostsGrid />
          {!unlocked && (
            <LockedBlock tab="content">
              <div className="grid grid-cols-3 gap-0.5 p-0.5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-line" />
                ))}
              </div>
            </LockedBlock>
          )}
        </>
      );
    case "reels":
      return (
        <>
          <ReelsGrid />
          {!unlocked && (
            <LockedBlock tab="content">
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="aspect-[9/16] rounded-lg bg-line" />
                ))}
              </div>
            </LockedBlock>
          )}
        </>
      );
    case "highlights":
      return (
        <>
          <HighlightsGrid />
          {!unlocked && (
            <LockedBlock tab="content">
              <div className="grid grid-cols-4 gap-x-4 gap-y-5 p-4 min-[480px]:grid-cols-6 sm:grid-cols-8 sm:px-5">
                {highlightItems.slice(0, 8).map((h, i) => (
                  <HighlightCircle key={i} label={h.label} gradient={h.gradient} img={h.img} />
                ))}
              </div>
            </LockedBlock>
          )}
        </>
      );
    case "reposts":
      return (
        <>
          <RepostsGrid />
          {!unlocked && (
            <LockedBlock tab="content">
              <div className="space-y-3 p-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-lg bg-line" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-40 rounded-full bg-line" />
                      <div className="h-2.5 w-28 rounded-full bg-line/70" />
                    </div>
                  </div>
                ))}
              </div>
            </LockedBlock>
          )}
        </>
      );
  }
}

function ContentBrowser({
  contentTab,
  onContentTabChange,
  hideContentTabs = false,
  unlocked = false,
}: {
  contentTab: PeeqdContentTabId;
  onContentTabChange: (tab: PeeqdContentTabId) => void;
  hideContentTabs?: boolean;
  unlocked?: boolean;
}) {
  return (
    <>
      {/*<ExportBanner />*/}
      {!hideContentTabs && (
        <UnderlineTabs
          tabs={PEEQD_CONTENT_TABS}
          active={contentTab}
          onChange={onContentTabChange}
          renderIcon={(id) => <ContentTabIcon tab={id} className="h-4 w-4 shrink-0" />}
          renderLabel={(tab) => tab.label}
        />
      )}
      <ContentBody tab={contentTab} unlocked={unlocked} />
    </>
  );
}

const TREND_UP_ICON = "/assets/icons/trend-up.svg";
const TREND_DOWN_ICON = "/assets/icons/trend-down.svg";

function TrendIcon({
  direction,
  className,
}: {
  direction: "up" | "down";
  className?: string;
}) {
  const src = direction === "up" ? TREND_UP_ICON : TREND_DOWN_ICON;

  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 bg-current ${className ?? "h-3 w-3"}`}
      style={{
        maskImage: `url(${src})`,
        WebkitMaskImage: `url(${src})`,
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskPosition: "center",
        WebkitMaskPosition: "center",
      }}
    />
  );
}

function formatPreviewCount(n: number): string {
  return n.toLocaleString("en-US");
}

export function FollowCountCards({
  side,
  onSideChange,
  stats,
}: {
  side: Side;
  onSideChange: (s: Side) => void;
  stats: Record<Side, SideStats>;
}) {
  return (
    <div className="py-3">
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#F6E9E7] p-1">
        {(["followers", "following"] as const).map((s) => {
          const data = stats[s];
          return (
            <button
              key={s}
              type="button"
              data-active={side === s ? "true" : "false"}
              className="pv-count rounded-lg px-3 py-3 text-left transition-all duration-200"
              onClick={() => onSideChange(s)}
            >
              <p className="pvc-label text-[13px] font-extrabold uppercase tracking-wider">
                {s === "followers" ? "Followers" : "Following"}
              </p>
              <p className="mt-1 font-display text-[28px] font-extrabold leading-none">
                {formatPreviewCount(data.total)}
              </p>
              <p className="mt-1 flex items-center justify-start gap-1 text-[12px] font-semibold text-rose-deep">
                <TrendIcon direction="up" className="h-5 w-5" />
                {data.gained} new in snapshot
              </p>
              <p className="flex items-center justify-start gap-1 text-[12px] text-muted">
                <TrendIcon direction="down" className="h-5 w-5" />
                {data.lost} unfollowed
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FollowFilterTabs({
  active,
  onChange,
  counts,
  allFirst = false,
}: {
  active: FollowFilter;
  onChange: (filter: FollowFilter) => void;
  counts: { new: number; lost: number; all: number };
  /** Homepage showcase only — All pill first. Production keeps default order. */
  allFirst?: boolean;
}) {
  const tabs: { id: FollowFilter; label: string }[] = allFirst
    ? [
        { id: "all", label: `All (${counts.all})` },
        { id: "new", label: `New (${counts.new})` },
        { id: "lost", label: `Lost (${counts.lost})` },
      ]
    : [
        { id: "new", label: `New (${counts.new})` },
        { id: "lost", label: `Lost (${counts.lost})` },
        { id: "all", label: `All (${counts.all})` },
      ];

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-1">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-active={active === tab.id ? "true" : "false"}
            onClick={() => onChange(tab.id)}
            className="rounded-full border border-rose px-3.5 py-1.5 text-[12.5px] font-semibold transition data-[active=false]:bg-surface data-[active=false]:text-rose-deep data-[active=true]:border-rose data-[active=true]:bg-rose data-[active=true]:text-white sm:px-4"
          >
            {tab.label}
          </button>
        ))}
      </div>
      <p className="flex shrink-0 items-center gap-1.5 text-[11.5px] text-muted">
        Sorted by: Most recent
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </p>
    </div>
  );
}

const FOLLOW_LIST_INSET = "px-4 sm:px-5";
const FOLLOW_LIST_MAX_HEIGHT = "max-h-[480px]";

export function FollowResultsCard({
  children,
  header,
  flush = false,
}: {
  children: ReactNode;
  header?: ReactNode;
  flush?: boolean;
}) {
  return (
    <div
      className={
        flush
          ? "overflow-hidden rounded-xl bg-white/70 ring-1 ring-black/[0.04]"
          : "overflow-hidden rounded-2xl border border-line bg-surface shadow-card"
      }
    >
      {header ? (
        <div className={`border-b border-line bg-rose-softer/30 ${FOLLOW_LIST_INSET} py-2.5`}>
          {header}
        </div>
      ) : null}
      <div className={`${FOLLOW_LIST_MAX_HEIGHT} overflow-y-auto`}>
        <div className={FOLLOW_LIST_INSET}>{children}</div>
      </div>
    </div>
  );
}

function FollowStatusPill({ status }: { status: "new" | "lost" }) {
  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white ${
        status === "new" ? "bg-rose" : "bg-[#B8AEB4]"
      }`}
    >
      {status === "new" ? "NEW" : "LOST"}
    </span>
  );
}

function FollowRankCircle({ rank }: { rank: number }) {
  const highlighted = rank <= 3;

  return (
    <span
      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
        highlighted ? "grad-bg text-white" : "bg-line text-muted"
      }`}
    >
      {rank}
    </span>
  );
}

export function FollowListRow({
  index,
  username,
  displayName,
  avatarIndex,
  avatarUrl,
  status,
  isRecent,
  useNumberedMock = false,
  mockPravatarIndex,
}: {
  index: number;
  username: string;
  displayName: string;
  avatarIndex: number;
  avatarUrl?: string | null;
  status?: "new" | "lost";
  isRecent?: boolean;
  useNumberedMock?: boolean;
  /** Homepage mock only - never set on live dashboard rows. */
  mockPravatarIndex?: number;
}) {
  const rank = index + 1;
  const handle = username.startsWith("@") ? username : `@${username}`;

  return (
    <a
      href={instagramProfileUrl(username)}
      {...INSTAGRAM_LINK_PROPS}
      className="flex items-center gap-3 py-2.5 transition hover:bg-rose-softer/40"
    >
      <FollowRankCircle rank={rank} />
      {!useNumberedMock &&
        (avatarUrl != null ? (
          <ListAvatar avatarUrl={avatarUrl} />
        ) : mockPravatarIndex != null ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={peeqdAvatarUrl(48, mockPravatarIndex)}
            alt=""
            className="h-12 w-12 shrink-0 rounded-full object-cover"
          />
        ) : (
          <ListAvatar avatarUrl={null} />
        ))}
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold">{handle}</p>
        <p className="text-[13px] text-muted">{displayName}</p>
      </div>
      {isRecent ? (
        <span className="shrink-0 rounded-full bg-rose px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          Recent
        </span>
      ) : status ? (
        <FollowStatusPill status={status} />
      ) : null}
    </a>
  );
}

function filterFollowList(list: ListItem[], filter: FollowFilter): ListItem[] {
  if (filter === "all") return list;
  return list.filter((item) => item.status === filter);
}

function followFilterCounts(list: ListItem[], total: number) {
  return {
    new: list.filter((item) => item.status === "new").length,
    lost: list.filter((item) => item.status === "lost").length,
    all: total,
  };
}

function UnfollowsBody({
  side,
  onSideChange,
  list,
  defaultFilter = "new",
  useNumberedMock = false,
  previewLimit = 3,
  showPaywall = true,
  listFlush = false,
  showcaseMode = false,
}: {
  side: Side;
  onSideChange: (s: Side) => void;
  list: ListItem[];
  defaultFilter?: FollowFilter;
  useNumberedMock?: boolean;
  /** Max rows before paywall in landing preview; omit paywall when unlimited. */
  previewLimit?: number;
  showPaywall?: boolean;
  listFlush?: boolean;
  /** Homepage showcase only — All default, All-first pills, mock gained/lost counts. */
  showcaseMode?: boolean;
}) {
  const [filter, setFilter] = useState<FollowFilter>(defaultFilter);
  const stats = PREVIEW_SIDE_STATS;
  const sideStats = stats[side];
  const counts = showcaseMode
    ? { new: sideStats.gained, lost: sideStats.lost, all: sideStats.total }
    : followFilterCounts(list, sideStats.total);
  const filtered = filterFollowList(list, filter);
  const limit = showPaywall
    ? previewLimit
    : showcaseMode && filter === "all"
      ? SHOWCASE_FOLLOW_LIST_LIMIT
      : FOLLOW_LIST_DISPLAY_LIMIT;
  const visible = filtered.slice(0, limit);
  const locked = showPaywall ? filtered.slice(limit) : [];

  useEffect(() => {
    setFilter(defaultFilter);
  }, [side, defaultFilter]);

  return (
    <div className="space-y-3">
      <FollowCountCards side={side} onSideChange={onSideChange} stats={stats} />
      <FollowFilterTabs
        active={filter}
        onChange={setFilter}
        counts={counts}
        allFirst={showcaseMode}
      />

      <FollowResultsCard
        header={
          filter === "all" && !showPaywall && visible.length > 0 ? (
            <p className="text-center text-[11.5px] font-medium text-muted">
              Showing {visible.length} most recent
            </p>
          ) : undefined
        }
        flush={listFlush}
      >
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-muted">
            No {filter} {side} this week.
          </div>
        ) : (
          <>
            <div className="divide-y divide-line">
              {visible.map((it, i) => (
                <FollowListRow
                  key={it.u}
                  index={i}
                  username={it.u}
                  displayName={it.n}
                  avatarIndex={it.img}
                  status={it.status}
                  useNumberedMock={useNumberedMock}
                  mockPravatarIndex={it.img}
                />
              ))}
            </div>

            {locked.length > 0 && (
              <LockedBlock tab="unfollows">
                <div className="divide-y divide-line">
                  {locked.map((it, i) => (
                    <FollowListRow
                      key={it.u}
                      index={i + visible.length}
                      username={it.u}
                      displayName={it.n}
                      avatarIndex={it.img}
                      status={it.status}
                      useNumberedMock={useNumberedMock}
                      mockPravatarIndex={it.img}
                    />
                  ))}
                </div>
              </LockedBlock>
            )}
          </>
        )}
      </FollowResultsCard>
    </div>
  );
}

function LikesBody({ unlocked = false }: { unlocked?: boolean }) {
  const visible = unlocked ? likesLeaderboard : likesLeaderboard.slice(0, 3);
  const locked = unlocked ? [] : likesLeaderboard.slice(3);

  return (
    <div>
      <div className="flex items-center gap-2.5 border-b border-line px-4 py-4 sm:px-5">
        <h3 className="font-display text-[22px] font-extrabold text-ink">Who Liked Their Posts</h3>
        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-mono text-[13px] font-bold text-ink">{LIKES_TOTAL}</span>
      </div>

      <div className="hidden grid-cols-[5rem_1fr_auto] gap-3 border-b border-line bg-rose-softer/40 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-muted sm:grid">
        <span>Place</span>
        <span>Username</span>
        <span className="text-right">Likes received</span>
      </div>

      {visible.map((row) => (
        <LikesLeaderboardRow key={row.user} row={row} />
      ))}

      {locked.length > 0 && (
        <LockedBlock tab="likes">
          <div>
            {locked.map((row) => (
              <LikesLeaderboardRow key={row.user} row={row} />
            ))}
          </div>
        </LockedBlock>
      )}
    </div>
  );
}

function ProfileCard({
  profile,
  loading,
  flushCard,
}: {
  profile: ReturnType<typeof buildProfilePreview>;
  loading: boolean;
  flushCard: boolean;
}) {
  const shell = flushCard
    ? "overflow-hidden"
    : "overflow-hidden rounded-2xl border border-line bg-surface shadow-card";

  return (
    <div className={shell}>
      <PeeqdProfileHeader profile={profile} loading={loading} />
    </div>
  );
}

function TabContentCard({
  children,
  flushCard = false,
}: {
  children: ReactNode;
  flushCard?: boolean;
}) {
  if (flushCard) return <>{children}</>;
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
      {children}
    </div>
  );
}

function MockupCard({
  section,
  contentTab,
  onContentTabChange,
  displayName,
  loading,
  side,
  onSideChange,
  list,
  hideContentTabs = false,
  flushCard = false,
  hideProfileHeader = false,
  unlocked = false,
  embedded = false,
  listFlush = false,
  showPaywall = true,
  showcaseMode = false,
}: {
  section: PeeqdReportTabId;
  contentTab: PeeqdContentTabId;
  onContentTabChange: (tab: PeeqdContentTabId) => void;
  displayName: string;
  loading: boolean;
  side: Side;
  onSideChange: (s: Side) => void;
  list: ListItem[];
  hideContentTabs?: boolean;
  flushCard?: boolean;
  hideProfileHeader?: boolean;
  unlocked?: boolean;
  embedded?: boolean;
  listFlush?: boolean;
  showPaywall?: boolean;
  showcaseMode?: boolean;
}) {
  const comingSoonLabel = embedded
    ? PEEQD_COMING_SOON_SECTIONS[section]
    : section === "comments"
      ? PEEQD_COMING_SOON_SECTIONS.comments
      : undefined;
  const showContentBrowser =
    !comingSoonLabel && (section === "stories" || section === "content");
  const splitLayout = !flushCard && hideProfileHeader;
  const handle = displayName.replace(/^@/, "");
  const profile = buildProfilePreview(handle);

  const bodyContent = comingSoonLabel ? (
    <ComingSoonCard label={comingSoonLabel} />
  ) : showContentBrowser ? (
    <ContentBrowser
      contentTab={contentTab}
      onContentTabChange={onContentTabChange}
      hideContentTabs={hideContentTabs}
      unlocked={unlocked}
    />
  ) : section === "unfollows" ? (
    <UnfollowsBody
      side={side}
      onSideChange={onSideChange}
      list={list}
      listFlush={listFlush}
      showPaywall={showPaywall}
      defaultFilter={showcaseMode ? "all" : "new"}
      showcaseMode={showcaseMode}
    />
  ) : (
    <LikesBody unlocked={unlocked} />
  );

  const contentSkeleton = (
    <>
      <div className="sk h-12" />
      <div className="space-y-2 p-4">
        <div className="sk h-32 rounded-xl" />
        <div className="sk h-32 rounded-xl" />
      </div>
    </>
  );

  if (loading) {
    if (splitLayout) {
      if (section === "unfollows") {
        return contentSkeleton;
      }
      return <TabContentCard flushCard={flushCard}>{contentSkeleton}</TabContentCard>;
    }

    return (
      <TabContentCard flushCard={flushCard}>
        {!hideProfileHeader && <PeeqdProfileHeader profile={profile} loading />}
        {contentSkeleton}
      </TabContentCard>
    );
  }

  if (splitLayout) {
    if (section === "unfollows") {
      return bodyContent;
    }
    return <TabContentCard flushCard={flushCard}>{bodyContent}</TabContentCard>;
  }

  return (
    <TabContentCard flushCard={flushCard}>
      {!hideProfileHeader && <PeeqdProfileHeader profile={profile} loading={false} />}
      {bodyContent}
    </TabContentCard>
  );
}

interface PeeqdPreviewProps {
  username: string;
  /** Homepage showcase: username frozen at CTA click — never refetch on every keystroke. */
  lookupUsername?: string | null;
  trigger: number;
  defaultTab?: PeeqdReportTabId;
  toolId?: PeeqdToolId;
  /** Card-only mode for tool-page dashboard zone (no outer chrome or tabs). */
  embedded?: boolean;
  section?: PeeqdReportTabId;
  contentTab?: PeeqdContentTabId;
  onContentTabChange?: (tab: PeeqdContentTabId) => void;
  hideContentTabs?: boolean;
  flushCard?: boolean;
  hideProfileHeader?: boolean;
  /** Authed dashboard - skip paywall gatekeepers on content tabs. */
  unlocked?: boolean;
  /** Homepage-style unified demo frame with gradient backdrop. */
  showcase?: boolean;
  /** Homepage showcase: reset to initial demo after a lookup error. */
  onShowcaseReset?: () => void;
}

export default function PeeqdPreview({
  username,
  lookupUsername = null,
  trigger,
  defaultTab,
  toolId,
  embedded = false,
  section: sectionProp,
  contentTab: contentTabProp,
  onContentTabChange,
  hideContentTabs = false,
  flushCard = false,
  hideProfileHeader = false,
  unlocked = false,
  showcase = false,
  onShowcaseReset,
}: PeeqdPreviewProps) {
  const { status } = useSession();
  const { openAuthModal } = useAuthModal();
  const router = useRouter();
  const resolvedDefault = defaultTab ?? getDefaultReportTab(toolId);
  const [sectionState, setSectionState] = useState<PeeqdReportTabId>(resolvedDefault);
  const [contentTabState, setContentTabState] = useState<PeeqdContentTabId>(
    PEEQD_SECTION_DEFAULT_CONTENT_TAB[resolvedDefault] ?? "stories"
  );
  const section = embedded && sectionProp ? sectionProp : sectionState;
  const contentTab =
    embedded && contentTabProp != null ? contentTabProp : contentTabState;
  const setContentTab = onContentTabChange ?? setContentTabState;
  const [loading, setLoading] = useState(false);
  const [fetchedProfile, setFetchedProfile] = useState<PeeqdProfileData | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [side, setSide] = useState<Side>("following");
  const [displayName, setDisplayName] = useState(`@${DEFAULT_USERNAME}`);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const useShowcase = showcase && !embedded;
  const showcaseSubmitted = useShowcase && trigger > 0;

  const handleUnlock = useCallback(() => {
    const resolvedUsername =
      (lookupUsername ?? username.trim().replace(/^@+/, "")) || undefined;
    trackFunnelEvent("monetization", "paywall_unlock_clicked", {
      tool_id: toolId,
      username: resolvedUsername,
      source: "showcase_gatekeeper",
      tab: sectionState,
    });

    if (status === "authenticated") {
      redirectTo("/pricing", REDIRECT_MESSAGES.pricing);
      return;
    }

    openAuthModal({
      authSource: "reveal_gatekeeper",
      callbackUrl: "/pricing",
      postAuthRedirect: "/pricing",
    });
  }, [
    openAuthModal,
    router,
    status,
    toolId,
    lookupUsername,
    username,
    sectionState,
  ]);

  useEffect(() => {
    if (embedded) return;
    setSectionState(resolvedDefault);
    setContentTabState(PEEQD_SECTION_DEFAULT_CONTENT_TAB[resolvedDefault] ?? "stories");
  }, [resolvedDefault, embedded]);

  const handleSectionChange = (tab: PeeqdReportTabId) => {
    setSectionState(tab);
    const defaultContent = PEEQD_SECTION_DEFAULT_CONTENT_TAB[tab];
    if (defaultContent) setContentTab(defaultContent);
  };

  const runPreview = useCallback(
    async (raw: string) => {
      const normalized = raw.trim().replace(/^@/, "") || DEFAULT_USERNAME;
      if (embedded) {
        setDisplayName(`@${normalized}`);
        setLoading(false);
        return;
      }

      if (useShowcase) {
        const normalized = raw.trim().replace(/^@/, "");
        if (!normalized) {
          setFetchError("Enter a valid Instagram username.");
          setLoading(false);
          return;
        }

        fetchAbortRef.current?.abort();
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        setDisplayName(`@${normalized}`);
        setLoading(true);
        setFetchError(null);
        setFetchedProfile(null);

        try {
          const res = await fetch(
            `/api/profile/lookup?username=${encodeURIComponent(normalized)}`,
            { signal: controller.signal }
          );
          const data = (await res.json()) as {
            profile?: PeeqdProfileData;
            error?: string;
          };
          if (!res.ok) {
            throw new Error(friendlyLookupError(data.error || SHOWCASE_LOOKUP_ERROR));
          }
          if (!data.profile) {
            throw new Error(SHOWCASE_LOOKUP_ERROR);
          }
          setFetchedProfile(data.profile);
          trackFunnelEvent("tool_engagement", "preview_loaded", {
            tool_id: toolId,
            username: normalized,
            source: "showcase",
          });
        } catch (err) {
          if (controller.signal.aborted) return;
          const message =
            err instanceof Error ? friendlyLookupError(err.message) : SHOWCASE_LOOKUP_ERROR;
          setFetchError(message);
          setFetchedProfile(null);
          trackFunnelEvent("tool_engagement", "preview_error", {
            tool_id: toolId,
            username: normalized,
            error: message,
            source: "showcase",
          });
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        }
        return;
      }

      setLoading(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setDisplayName(`@${normalized}`);
        setLoading(false);
        timerRef.current = null;
      }, 1400);
    },
    [embedded, useShowcase, toolId]
  );

  useEffect(() => {
    if (trigger <= 0) return;

    if (useShowcase) {
      if (lookupUsername) runPreview(lookupUsername);
    } else {
      runPreview(username);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      fetchAbortRef.current?.abort();
    };
  }, [trigger, lookupUsername, username, useShowcase, runPreview]);

  const baseList = side === "following" ? followingList : followersList;
  const list = useShowcase ? buildShowcaseFollowList(side) : baseList;
  const previewHandle = displayName.replace(/^@/, "") || DEFAULT_USERNAME;
  const showcaseDemo = useShowcase && !showcaseSubmitted;
  const profile = showcaseDemo
    ? buildProfilePreview(DEFAULT_USERNAME)
    : useShowcase && showcaseSubmitted && fetchedProfile
      ? fetchedProfile
      : buildProfilePreview(previewHandle);
  const showcaseLoading = useShowcase && showcaseSubmitted && loading && !fetchError;
  const showcaseGatekeeper =
    useShowcase && showcaseSubmitted && !loading && !fetchError && fetchedProfile != null;

  useEffect(() => {
    if (!showcaseGatekeeper) return;
    const resolvedUsername =
      (lookupUsername ?? username.trim().replace(/^@+/, "")) || undefined;
    trackFunnelEvent("monetization", "paywall_shown", {
      tool_id: toolId,
      username: resolvedUsername,
      source: "showcase",
      tab: section,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showcaseGatekeeper, trigger, lookupUsername]);
  const showcaseError = useShowcase && showcaseSubmitted && !loading && fetchError != null;
  const landingLayout = !embedded;
  const mockShowFull = unlocked || useShowcase;
  const mockupFlush = flushCard || useShowcase;
  const mockupHideProfile = hideProfileHeader || landingLayout || useShowcase;
  const showcaseExcludeTabs: PeeqdReportTabId[] = useShowcase ? ["stories", "content"] : [];

  const card = (
    <>
      {loading && !useShowcase && <ProcessingBar label={LOADING_LABELS[section]} />}
      <MockupCard
        section={section}
        contentTab={contentTab}
        onContentTabChange={setContentTab}
        displayName={displayName}
        loading={loading}
        side={side}
        onSideChange={setSide}
        list={list}
        hideContentTabs={hideContentTabs}
        flushCard={mockupFlush}
        hideProfileHeader={mockupHideProfile}
        unlocked={mockShowFull}
        embedded={embedded}
        listFlush={useShowcase}
        showPaywall={!useShowcase}
        showcaseMode={useShowcase}
      />
    </>
  );

  if (embedded) {
    return <div className="space-y-4">{card}</div>;
  }

  if (useShowcase) {
    return (
      <section className="mx-auto max-w-3xl overflow-visible pb-8">
        {/*}
        <div className="mb-5 text-center">
          <span className="text-[12px] font-semibold uppercase tracking-widest text-rose-deep">
            Example
          </span>
        </div>
        */}

        <div className="relative px-2 py-1 sm:px-4 sm:py-1">
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
            <div className="absolute -right-12 -top-10 h-64 w-64 rounded-full bg-gradient-to-bl from-rose/35 via-rose/15 to-transparent blur-3xl" />
            <div className="absolute -bottom-16 -left-14 h-72 w-72 rounded-full bg-gradient-to-tr from-violet/30 via-rose/15 to-transparent blur-3xl" />
            <div className="absolute left-1/4 top-1/2 h-44 w-44 -translate-y-1/2 rounded-full bg-rose-soft/80 blur-2xl" />
          </div>

          {showcaseError ? (
            <ShowcaseLookupError message={fetchError} onRetry={onShowcaseReset} />
          ) : (
          <div className="relative z-10 overflow-hidden rounded-3xl border border-line/80 bg-surface/95 shadow-card ring-1 ring-black/[0.03] backdrop-blur-sm">
            <ProfileCard profile={profile} loading={showcaseLoading} flushCard />

            <ShowcaseGatekeeper
              active={showcaseGatekeeper}
              tab={section}
              onUnlock={handleUnlock}
            >
              <div className="px-4 sm:px-5">
                {showcaseLoading ? (
                  <ChangesSummaryCard
                    kind="weekly"
                    weekLabel={SHOWCASE_SUMMARY.weekLabel}
                    isCollecting
                  />
                ) : (
                  <ChangesSummaryCard data={SHOWCASE_SUMMARY} kind="weekly" />
                )}
              </div>
              <div className="flex justify-center border-b border-line bg-gradient-to-b from-rose-softer/50 to-surface px-4 py-3.5 sm:px-5">
                <SectionPillTabs
                  active={section}
                  onChange={handleSectionChange}
                  excludeTabs={showcaseExcludeTabs}
                />
              </div>
              <div className="space-y-3 px-4 py-4 sm:px-5 sm:py-5">
                {showcaseLoading && (
                  <ProcessingBar label={LOADING_LABELS[section]} />
                )}
                <MockupCard
                  section={section}
                  contentTab={contentTab}
                  onContentTabChange={setContentTab}
                  displayName={displayName}
                  loading={showcaseLoading}
                  side={side}
                  onSideChange={setSide}
                  list={list}
                  hideContentTabs={hideContentTabs}
                  flushCard
                  hideProfileHeader
                  unlocked={mockShowFull}
                  embedded={embedded}
                  listFlush
                  showPaywall={false}
                  showcaseMode
                />
              </div>
            </ShowcaseGatekeeper>
          </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl pb-8">
      <div className="mb-4 text-center">
        <span className="text-[12px] font-semibold uppercase tracking-widest text-rose-deep">Example</span>
      </div>

      <div className="space-y-4">
        <ProfileCard profile={profile} loading={loading} flushCard={false} />
        <SectionPillTabs active={section} onChange={handleSectionChange} />
        {card}
      </div>
    </section>
  );
}
